import { v4 as uuidv4 } from 'uuid';
import type { LabelScheme, Plan, PlanIndex, Point, Row, Seat, Shape, ShapeKind, Zone } from './types';

export const SEAT_RADIUS = 10;
export const ROW_LABEL_DISTANCE = 25;

export const seatKey = (seat: Seat): string => seat.uuid || seat.seat_guid || '';
export const shapeKey = (kind: ShapeKind, id: string) => `${kind}:${id}`;

export const add = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Point, k: number): Point => ({ x: a.x * k, y: a.y * k });
export const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
export const round2 = (v: number) => Math.round(v * 100) / 100;
export const roundPoint = (p: Point): Point => ({ x: round2(p.x), y: round2(p.y) });

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

export const normalizeAngle = (deg: number) => {
    let a = deg % 360;
    if (a > 180) a -= 360;
    if (a <= -180) a += 360;
    return a;
};

export const rotatePoint = (p: Point, c: Point, deg: number): Point => {
    const r = toRad(deg);
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
};

export const snapValue = (v: number, grid: number) => Math.round(v / grid) * grid;
export const snapPoint = (p: Point, grid: number): Point => ({ x: snapValue(p.x, grid), y: snapValue(p.y, grid) });

export const snapAngle = (from: Point, to: Point, stepDeg: number): Point => {
    const len = dist(from, to);
    const a = Math.round(toDeg(Math.atan2(to.y - from.y, to.x - from.x)) / stepDeg) * stepDeg;
    return { x: from.x + Math.cos(toRad(a)) * len, y: from.y + Math.sin(toRad(a)) * len };
};

export type Rect = { x: number; y: number; width: number; height: number };

export const rectFromPoints = (a: Point, b: Point): Rect => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
});

export const pointInRect = (p: Point, r: Rect) => p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height;

export const rectsIntersect = (a: Rect, b: Rect) =>
    a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;

export const boundsOf = (points: Point[], pad = 0): Rect | null => {
    if (!points.length) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
    }
    return { x: minX - pad, y: minY - pad, width: maxX - minX + pad * 2, height: maxY - minY + pad * 2 };
};

export const shapeCenter = (s: Shape): Point => ({
    x: s.position.x + s.size.width / 2,
    y: s.position.y + s.size.height / 2,
});

export const shapeCorners = (s: Shape): Point[] => {
    const c = shapeCenter(s);
    const { x, y } = s.position;
    const { width: w, height: h } = s.size;
    return [
        { x, y },
        { x: x + w, y },
        { x: x + w, y: y + h },
        { x, y: y + h },
    ].map(p => rotatePoint(p, c, s.rotation || 0));
};

export const shapeBounds = (s: Shape): Rect => boundsOf(shapeCorners(s))!;

export const rowOrigin = (zone: Zone, row: Row): Point => ({
    x: (zone.position?.x ?? 0) + (row.position?.x ?? 0),
    y: (zone.position?.y ?? 0) + (row.position?.y ?? 0),
});

export const seatAbs = (zone: Zone, row: Row, seat: Seat): Point => {
    const o = rowOrigin(zone, row);
    return { x: o.x + (seat.position?.x ?? 0), y: o.y + (seat.position?.y ?? 0) };
};

const toAlpha = (n: number, upper: boolean) => {
    let s = '';
    let v = Math.max(0, Math.floor(n));
    do {
        s = String.fromCharCode((v % 26) + 65) + s;
        v = Math.floor(v / 26) - 1;
    } while (v >= 0);
    return upper ? s : s.toLowerCase();
};

export const makeLabel = (i: number, scheme: LabelScheme, start = 1): string => {
    switch (scheme) {
        case 'upper':
            return toAlpha(start - 1 + i, true);
        case 'lower':
            return toAlpha(start - 1 + i, false);
        case 'odd': {
            const s = start % 2 === 1 ? start : start + 1;
            return String(s + i * 2);
        }
        case 'even': {
            const s = start % 2 === 0 ? start : start + 1;
            return String(s + i * 2);
        }
        default:
            return String(start + i);
    }
};

export const LABEL_SCHEMES: { value: LabelScheme; label: string }[] = [
    { value: 'numeric', label: '1, 2, 3' },
    { value: 'upper', label: 'A, B, C' },
    { value: 'lower', label: 'a, b, c' },
    { value: 'odd', label: '1, 3, 5' },
    { value: 'even', label: '2, 4, 6' },
];

export const normalizePlan = (raw: unknown): Plan => {
    const src = (raw && typeof raw === 'object' ? raw : {}) as Partial<Plan>;
    const zones: Zone[] = Array.isArray(src.zones) && src.zones.length
        ? src.zones.map(z => ({
            ...z,
            name: z.name ?? 'Zone',
            position: z.position ?? { x: 0, y: 0 },
            rows: (Array.isArray(z.rows) ? z.rows : []).map(r => ({
                ...r,
                uuid: r.uuid || uuidv4(),
                row_number: r.row_number ?? '',
                position: r.position ?? { x: 0, y: 0 },
                seats: (Array.isArray(r.seats) ? r.seats : []).map(s => {
                    if (s.uuid || s.seat_guid) return { ...s, position: s.position ?? { x: 0, y: 0 } };
                    const id = uuidv4();
                    return { ...s, uuid: id, seat_guid: id, position: s.position ?? { x: 0, y: 0 } };
                }),
            })),
        }))
        : [{ name: 'Ground Floor', code: 'GF', position: { x: 0, y: 0 }, rows: [] }];

    return {
        ...src,
        name: src.name ?? 'New Seat Map',
        categories: Array.isArray(src.categories) ? src.categories : [],
        zones,
        stages: (src.stages ?? []).map(s => ({ ...s, id: s.id || uuidv4() })),
        entrances: (src.entrances ?? []).map(e => ({ ...e, id: e.id || uuidv4() })),
    };
};

export const emptyPlan = (): Plan => normalizePlan({
    name: 'New Seat Map',
    categories: [
        { name: 'General', color: '#3B82F6' },
        { name: 'VIP', color: '#F59E0B' },
        { name: 'Premium', color: '#8B5CF6' },
    ],
});

export const buildIndex = (plan: Plan): PlanIndex => {
    const seats = new Map();
    const rows = new Map();
    const seatRow = new Map();
    const shapes = new Map();
    plan.zones.forEach((zone, zi) => {
        zone.rows.forEach((row, ri) => {
            const rowId = row.uuid!;
            rows.set(rowId, { zi, ri });
            row.seats.forEach((seat, si) => {
                const key = seatKey(seat);
                seats.set(key, { zi, ri, si });
                seatRow.set(key, rowId);
            });
        });
    });
    (plan.stages ?? []).forEach((s, index) => shapes.set(shapeKey('stage', s.id), { kind: 'stage', index }));
    (plan.entrances ?? []).forEach((s, index) => shapes.set(shapeKey('entrance', s.id), { kind: 'entrance', index }));
    return { seats, rows, seatRow, shapes };
};

export const getShape = (plan: Plan, key: string): Shape | undefined => {
    const [kind, id] = splitShapeKey(key);
    const list: Shape[] = kind === 'stage' ? plan.stages ?? [] : plan.entrances ?? [];
    return list.find(s => s.id === id);
};

export const splitShapeKey = (key: string): [ShapeKind, string] => {
    const i = key.indexOf(':');
    return [key.slice(0, i) as ShapeKind, key.slice(i + 1)];
};

export type RowGeometry = {
    apex: Point;
    angle: number;
    count: number;
    spacing: number;
    sweep: number;
};

const circumcenter = (a: Point, b: Point, c: Point): Point | null => {
    const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
    if (Math.abs(d) < 1e-6) return null;
    const a2 = a.x * a.x + a.y * a.y;
    const b2 = b.x * b.x + b.y * b.y;
    const c2 = c.x * c.x + c.y * c.y;
    return {
        x: (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d,
        y: (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d,
    };
};

export const measureRow = (points: Point[], fallbackSpacing: number): RowGeometry => {
    const n = points.length;
    if (n === 0) return { apex: { x: 0, y: 0 }, angle: 0, count: 0, spacing: fallbackSpacing, sweep: 0 };
    if (n === 1) return { apex: points[0], angle: 0, count: 1, spacing: fallbackSpacing, sweep: 0 };

    const first = points[0];
    const last = points[n - 1];
    let spacing = 0;
    for (let i = 1; i < n; i++) spacing += dist(points[i - 1], points[i]);
    spacing = spacing / (n - 1) || fallbackSpacing;

    const chordLen = dist(first, last);
    const chordMid = scale(add(first, last), 0.5);
    const angle = chordLen > 1e-6
        ? toDeg(Math.atan2(last.y - first.y, last.x - first.x))
        : toDeg(Math.atan2(points[1].y - first.y, points[1].x - first.x)) + 90;

    if (n < 3) return { apex: chordMid, angle, count: n, spacing, sweep: 0 };

    const mid = points[Math.floor(n / 2)];
    const center = circumcenter(first, mid, last);
    const u = { x: Math.cos(toRad(angle)), y: Math.sin(toRad(angle)) };
    const nrm = { x: -u.y, y: u.x };
    const midOffset = (mid.x - chordMid.x) * nrm.x + (mid.y - chordMid.y) * nrm.y;

    if (!center || Math.abs(midOffset) < 0.5) return { apex: chordMid, angle, count: n, spacing, sweep: 0 };

    const R = dist(center, first);
    const sign = -Math.sign(midOffset);
    const centerOffset = (center.x - chordMid.x) * nrm.x + (center.y - chordMid.y) * nrm.y;
    const minor = 2 * Math.asin(Math.min(1, chordLen / (2 * R)));
    const major = Math.sign(centerOffset) === Math.sign(midOffset) && Math.abs(centerOffset) > 1e-6;
    const sweepRad = chordLen < 1e-6 ? 2 * Math.PI : major ? 2 * Math.PI - minor : minor;
    const apex = add(center, scale(nrm, -sign * R));
    return { apex, angle, count: n, spacing, sweep: sign * toDeg(sweepRad) };
};

export const layoutRow = (g: RowGeometry): Point[] => {
    const { apex, count: n, spacing: d } = g;
    if (n <= 0) return [];
    const u = { x: Math.cos(toRad(g.angle)), y: Math.sin(toRad(g.angle)) };
    const nrm = { x: -u.y, y: u.x };
    const sweep = Math.max(-350, Math.min(350, g.sweep));

    if (n === 1 || Math.abs(sweep) < 0.05) {
        return Array.from({ length: n }, (_, i) => add(apex, scale(u, (i - (n - 1) / 2) * d)));
    }

    const s = Math.sign(sweep);
    const phi = toRad(Math.abs(sweep)) / (n - 1);
    const R = d / (2 * Math.sin(phi / 2));
    const center = add(apex, scale(nrm, s * R));
    return Array.from({ length: n }, (_, i) => {
        const t = -toRad(Math.abs(sweep)) / 2 + i * phi;
        return add(center, add(scale(nrm, -s * R * Math.cos(t)), scale(u, R * Math.sin(t))));
    });
};

export const rowLabelPositions = (points: Point[]): { left: Point; right: Point } | null => {
    if (!points.length) return null;
    const first = points[0];
    const last = points[points.length - 1];
    const a = points.length > 1 ? Math.atan2(points[1].y - first.y, points[1].x - first.x) : 0;
    const off = { x: Math.cos(a) * ROW_LABEL_DISTANCE, y: Math.sin(a) * ROW_LABEL_DISTANCE };
    return { left: sub(first, off), right: add(last, off) };
};

export const nextNumericLabel = (labels: string[], fallback: number) => {
    const nums = labels.map(l => parseInt(l, 10)).filter(n => Number.isFinite(n));
    return nums.length ? Math.max(...nums) + 1 : fallback;
};
